import fontAwesomeKitUrl from '/node_modules/@awesome.me/kit-ce81ba491f/icons/js/all.min.js?url';
import { livewireHook, whenLivewireReady } from './livewire-bridge.js';

let fontAwesomeKitPromise = null;
let livewireHooksRegistered = false;
let livewireMorphedCleanup = null;

function resolveMorphedNode(payload) {
    return payload?.component?.el ?? payload?.el ?? payload?.target ?? document;
}

// The map SVG contains zero FA markup (06.2), yet its 261 KB injection used to pay
// a document-wide dom.watch() MutationObserver scan. 06.3 A5.5 removed the global
// watcher (see loadFontAwesomeKit + FontAwesomeConfig.observeMutations=false in
// the layout); the injection is a loader innerHTML inside wire:ignore — never a
// Livewire morph — so the morph-scoped refresh below never touches it.
//
// Do NOT skip map.map-zone morphs here: the component's TOOLBARS (edit tools,
// panzoom controls, add-page) carry FA <i> icons, and a morph replaces their
// converted <svg>s with fresh <i> markup — with the refresh skipped they stayed
// untransformed (icons vanished after ALT-TAB fired the queued wire:polls,
// 2026-07-12).
function refreshFontAwesome(node = document) {
    if (window.FontAwesome?.dom?.i2svg) {
        window.FontAwesome.dom.i2svg({ node });
    }
}

function registerLivewireRerenderHooks() {
    if (livewireHooksRegistered) {
        return;
    }

    const handleNavigated = () => {
        refreshFontAwesome();
    };

    document.addEventListener('livewire:navigated', handleNavigated);

    const attachMorphedHook = () => {
        if (livewireMorphedCleanup) {
            return;
        }

        livewireMorphedCleanup = livewireHook('morphed', (payload) => {
            refreshFontAwesome(resolveMorphedNode(payload));
        });
    };

    whenLivewireReady(attachMorphedHook);
    attachMorphedHook();

    livewireHooksRegistered = true;

    // HMR cleanup for development
    if (import.meta.hot) {
        import.meta.hot.dispose(() => {
            document.removeEventListener('livewire:navigated', handleNavigated);
            if (livewireMorphedCleanup && typeof livewireMorphedCleanup === 'function') {
                livewireMorphedCleanup();
            }
            livewireHooksRegistered = false;
            livewireMorphedCleanup = null;
        });
    }
}

export function loadFontAwesomeKit() {
    if (window.FontAwesome?.dom) {
        registerLivewireRerenderHooks();
        refreshFontAwesome();
        return Promise.resolve(window.FontAwesome);
    }

    if (fontAwesomeKitPromise) {
        return fontAwesomeKitPromise;
    }

    fontAwesomeKitPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[data-fontawesome-kit="ce81ba491f"]');

        if (existingScript) {
            existingScript.crossOrigin = 'anonymous';

            existingScript.addEventListener('load', () => {
                registerLivewireRerenderHooks();
                refreshFontAwesome();
                resolve(window.FontAwesome);
            }, { once: true });

            existingScript.addEventListener('error', () => {
                reject(new Error('Font Awesome kit failed to load.'));
            }, { once: true });

            return;
        }

        const kitScript = document.createElement('script');
        kitScript.src = fontAwesomeKitUrl;
        kitScript.defer = true;
        kitScript.crossOrigin = 'anonymous';
        kitScript.dataset.fontawesomeKit = 'ce81ba491f';

        kitScript.addEventListener('load', () => {
            registerLivewireRerenderHooks();
            refreshFontAwesome();
            resolve(window.FontAwesome);
        }, { once: true });

        kitScript.addEventListener('error', () => {
            reject(new Error('Font Awesome kit failed to load.'));
        }, { once: true });

        document.head.appendChild(kitScript);
    }).catch((error) => {
        fontAwesomeKitPromise = null;
        console.error('[FontAwesome] Unable to load kit script:', error);
        throw error;
    });

    return fontAwesomeKitPromise;
}
